import api from '../../../utils/api';
import { getAssessmentReleaseStatus } from '../assessmentRelease';

/** A test whose deadline has passed: the message comes from the server. */
export class DeadlineError extends Error {
  constructor(message) {
    super(message || 'The deadline for this test has passed.');
    this.name = 'DeadlineError';
  }
}

// The chat says itself when something didn't go through (the waiting-answers note, the finish
// card), so these calls skip the app's error toast. A request that hangs on a weak connection
// gives up after a while, so the answers outbox can try again instead of waiting forever.
const QUIET = { skipErrorToast: true, timeout: 25000 };

const isDeadline = (error) =>
  error?.response?.status === 403 &&
  (error.response.data?.deadline_expired || String(error.response.data?.error || '').toLowerCase().includes('deadline'));

/** The student's saved progress for one test, or null. */
export const fetchProgress = async (type) => {
  const response = await api.get('/api/get-assessment-progress/', QUIET);
  const data = response?.data || {};
  if (data.assessment_progress && data.assessment_progress[type]) return data.assessment_progress[type];
  if (Array.isArray(data)) {
    const row = data.find((s) => s.assessment_type === type && s.status === 'in_progress');
    if (row) {
      return {
        session_id: row.id,
        status: row.status,
        current: Array.isArray(row.responses?.answers) ? row.responses.answers.length : row.current_question || 0,
        total: row.total_questions,
        responses: row.responses,
      };
    }
  }
  return null;
};

/**
 * Where to pick up: { sessionId, nextIndex, responses }. A finished session is not resumed.
 * `current` is the number of questions already answered, so it is also the next index.
 */
export const resumePoint = (progress, total) => {
  if (!progress || progress.status === 'completed' || !progress.session_id) {
    return { sessionId: null, nextIndex: 0, responses: null };
  }
  const current = Number(progress.current) || 0;
  if (current > 0 && current < total) {
    return { sessionId: progress.session_id, nextIndex: current, responses: progress.responses || null };
  }
  return { sessionId: progress.session_id, nextIndex: 0, responses: null };
};

/** The session id to save into, starting one if the chat didn't. */
export const ensureSession = async (type, total, knownId) => {
  if (knownId) return knownId;
  try {
    const response = await api.post('/api/start-assessment-session/', { assessment_type: type, total_questions: total }, QUIET);
    const id = response?.data?.session?.id;
    if (!id) throw new Error('No session id returned');
    return id;
  } catch (error) {
    if (isDeadline(error)) throw new DeadlineError(error.response.data?.error);
    throw error;
  }
};

export const saveProgress = (payload) => api.post('/api/update-assessment-progress/', payload, QUIET);

export const completeSession = (sessionId, payload) =>
  api.post(`/api/complete-assessment-session/${sessionId}/`, payload, { ...QUIET, timeout: 45000 });

/** released / in_review / not_shared for a finished session (fails closed to in_review). */
export const releaseStatus = (type, sessionId) => getAssessmentReleaseStatus(type, sessionId);
